// Single source of truth for brand/contact facts used in <meta> tags and
// JSON-LD structured data. Every value here must be independently visible
// somewhere on the live site (Footer, ContactDetails, Home hero) -- do not
// add anything here that the site itself doesn't already state publicly.
// See src/config/schema.js for the JSON-LD graph builders that consume this.

export const DOMAIN = 'https://pixelcraft.fi';

// Canonical rule: apex host (no www), no query/hash, no trailing slash
// except for the root path itself. The single source of truth for both
// <link rel="canonical"> (PageSEO.jsx) and every JSON-LD @id/url
// (config/schema.js) -- they must never be able to drift apart.
export const normalizePath = (path) => {
  const clean = String(path).split('?')[0].split('#')[0];
  if (clean.length > 1 && clean.endsWith('/')) return clean.slice(0, -1);
  return clean || '/';
};

export const canonicalUrl = (path) => `${DOMAIN}${normalizePath(path)}`;

// Joins DOMAIN to a webpack-resolved asset path (e.g. a project screenshot
// imported via `import img from '../img/x.webp'`). Real production builds
// always emit paths starting with "/", but defends against that not being
// guaranteed everywhere (e.g. CRA's Jest asset mock in tests returns a bare
// filename) rather than risk a missing separator producing a broken URL.
export const absoluteAssetUrl = (assetPath) =>
  `${DOMAIN}${assetPath.startsWith('/') ? '' : '/'}${assetPath}`;

export const SITE = {
  name: 'PixelCraft',
  domain: DOMAIN,
  // Derived from the real, currently-listed Home services (Web
  // Development, Web Design, Photography) and areaServed -- fixed here
  // (rather than passed in per-page) so the Organization node is
  // byte-identical, not just same-@id, on every page's graph.
  description: 'Freelance web design, web development, and photography services, serving clients in Finland.',
  // Footer ("PixelCraft"), PageSEO title suffix -- no separate legal entity
  // name appears anywhere on the site, so we don't invent one.
  email: 'info@pixelcraft.fi',
  telephone: '+358468130887', // ContactDetails.js / Footer.js (tel: links)
  sameAs: [
    // Only profiles that are actually linked from the site chrome
    // (Footer.js). The footer's GitHub link (github.com/maikamp-ven) is the
    // developer's personal/dev account, not a PixelCraft brand profile --
    // deliberately excluded per Sprint 7 review.
    'https://www.instagram.com/pixelcraft.fi/',
    'https://www.facebook.com/profile.php?id=61567865667907',
    'https://t.me/pixelcraft_fi',
  ],
  // Confirmed only indirectly (FI phone prefix, EUR pricing, 25.5% Finnish
  // VAT quoted throughout packages.json/faq.json) -- no explicit "we serve
  // Finland" statement exists, but every signal on the site points here and
  // none point elsewhere, so this is a safe, defensible areaServed value.
  areaServed: 'FI',
  inLanguages: ['en', 'ru', 'fi'],
  logo: {
    // The same file Navbar.js uses (src/img/logo.webp), also copied to
    // public/logo.webp as a stable, unhashed URL -- schema.org's `logo`
    // needs one predictable production URL, and a webpack-hashed import
    // would (a) not resolve from a plain Node script like
    // scripts/verify/verify-schema.js, which has no webpack/Jest transform for
    // asset imports, and (b) change on every rebuild for no reason, since
    // this file isn't code that needs cache-busting.
    url: `${DOMAIN}/logo.webp`,
    width: 90,
    height: 90,
  },
  ogImage: {
    // public/ static asset (not webpack-processed), already referenced this
    // way in PageSEO.jsx.
    url: `${DOMAIN}/og-image.png`,
    width: 1200,
    height: 630,
  },
};

// The only person named/represented on the site (Home hero: "Hi, my name is
// Elena ... freelancer specializing in web design, development, and SEO").
// No surname, photo, or dedicated About page exists -- keep this minimal and
// don't invent fields the site doesn't state.
export const FOUNDER = {
  name: 'Elena',
  jobTitle: ['Web Developer'],
};